/* eslint-disable @typescript-eslint/camelcase */
import isArray from 'lodash/isArray';

import {
	SITECORE_PAGES,
	PRODUCT_CLASSIFICATION,
	ACCOUNT_STATUS,
} from 'core/constants';

import { AccountType, MenuItem, TargetItem } from '../../Header.sitecore';

/**
 * This method returns a lowercase version of account type value
 * @param {string} account - account type by logged in member.
 */
const getAccountTypeLongerName = (account: string) => {
	const accountToLowerCase = account.toLowerCase();
	let formattedAccountType;
	// accum here is a special case, its just a fallback if sitecore will not pass the correct value which is 'accumulation'
	switch (accountToLowerCase) {
		case 'accum' || PRODUCT_CLASSIFICATION.ACCUMULATION.toLowerCase():
			formattedAccountType = PRODUCT_CLASSIFICATION.ACCUMULATION.toLowerCase();
			break;
		case PRODUCT_CLASSIFICATION.TTR.toLowerCase() ||
			PRODUCT_CLASSIFICATION.PENSION.toLowerCase():
			formattedAccountType = PRODUCT_CLASSIFICATION.PENSION.toLowerCase();
			break;
		default:
			formattedAccountType = accountToLowerCase;
			break;
	}

	return formattedAccountType;
};

/**
 * This method returns a boolean whether to show/hide an item on navigation menu
 * @param {string} accountType - account type by logged in member.
 * @param {array} acctTypeValues - list of account types that are allowed see specific menu item.
 */
export const checkNavigationFilterByAccountType = (
	accountType: string,
	acctTypeValues: AccountType | undefined,
) => {
	if (!acctTypeValues) return false;

	const targetItems = acctTypeValues?.targetItems;
	const hideFromNav = targetItems.filter(
		(t: TargetItem) =>
			getAccountTypeLongerName(t.displayName) === accountType.toLowerCase(),
	);

	return hideFromNav?.length === 0 ? true : false;
};

/**
 * This method returns a filtered array based on account type of pending profile members - Level 1
 * @param {string} accountType - account type by logged in member.
 * @param {array} homeItemChildren - children items of home menu item.
 */
export const filterLevel1ItemsByAccountType = (
	accountType: string,
	homeItemChildren?: MenuItem[],
) => {
	if (!accountType || !homeItemChildren?.length) return [];

	const pendingProfileLevel1Urls = [
		SITECORE_PAGES.ACCOUNT as string,
		SITECORE_PAGES.TRANSACTIONS as string,
	];

	const homeMenu =
		accountType.toLowerCase() ===
		PRODUCT_CLASSIFICATION.ACCUMULATION.toLowerCase()
			? homeItemChildren.filter(a => pendingProfileLevel1Urls.includes(a.url))
			: homeItemChildren.filter(a => a.url === pendingProfileLevel1Urls[0]);

	return homeMenu;
};

/**
 * This method returns a false if member is not allowed to see deeplinking URL
 * @param {string} accountType - product name associated to logged in user.
 * @param {string} gotoUrl - current URL user wants to access.
 * @param {array} menuItems - menu items of the relevant level 1 menu.
 */
export const checkIfPageIsAllowed = (
	accountType: string,
	gotoUrl: string,
	menuItems?: MenuItem[],
): boolean => {
	const menuChildItems: MenuItem[] = [];

	if (isArray(menuItems)) {
		const gotoUrlPath = gotoUrl && gotoUrl.split('/');
		const pathToFilter = `/${gotoUrlPath[1]}`;
		let arrayCounts = 0;

		// Level 1 - menu items, values you can see on the header navigation
		const gotoUrlItem = menuItems.filter(
			a => a.children && a?.children?.length > 0 && a.url === pathToFilter,
		);

		if (gotoUrlItem?.length > 0) {
			arrayCounts += gotoUrlItem?.length;
			menuChildItems.push(...gotoUrlItem);
		}

		// Level 2 - child items of Level 1 menu item
		// TODO: to think of another way or faster way to filter this out.
		gotoUrlItem?.forEach(lvl2MenuItem => {
			if (lvl2MenuItem.children && lvl2MenuItem?.children?.length > 0) {
				arrayCounts += lvl2MenuItem?.children?.length;
				const level3ChildItem = lvl2MenuItem.children;
				menuChildItems.push(...level3ChildItem);

				// Level 3 - child items of Level 2 menu item
				level3ChildItem?.forEach(lvl3MenuItem => {
					if (lvl3MenuItem.children && lvl3MenuItem?.children?.length > 0) {
						arrayCounts += lvl3MenuItem?.children?.length;
						const level4ChildItem = lvl3MenuItem.children;
						menuChildItems.push(...lvl3MenuItem.children);

						level4ChildItem?.forEach(lvl4MenuItem => {
							if (lvl4MenuItem.children && lvl4MenuItem?.children?.length > 0) {
								arrayCounts += lvl4MenuItem?.children?.length;
								menuChildItems.push(...lvl4MenuItem.children);
							}
						});
					}
				});
			}
		});

		// We need to wait for the array counts to be equal to the menu items array then we search if user is valid to see it or not
		if (arrayCounts === menuChildItems?.length) {
			const accessiblePageItem =
				gotoUrlItem &&
				menuChildItems?.filter(
					mchItem =>
						mchItem.url === gotoUrl &&
						!checkNavigationFilterByAccountType(accountType, mchItem.acctType),
				);

			// returns true if user is allowed to see the target deeplinking URL
			if (accessiblePageItem && accessiblePageItem?.length > 0) {
				return true;
			} else {
				return false;
			}
		}
	}

	return false;
};

/**
 * This method returns a filtered array based on account type of pending profile members - Level 2
 * @param {string} productName - account type by logged in member.
 * @param {array} restItemsRaw - children items of home menu item.
 */
export const filterLevel2ItemsByAccountType = (
	restItemsRaw: MenuItem[],
	productClassification: string,
	balance: string | number,
) => {
	if (!restItemsRaw?.length) return [];

	const level2AccountUrls = [
		SITECORE_PAGES.WHO_GETS_YOUR_MONEY as string,
		SITECORE_PAGES.WHO_GETS_YOUR_BENEFIT as string,
		SITECORE_PAGES.CONSOLIDATE_SUPER as string,
		SITECORE_PAGES.TAX_FILE_NUMBER as string,
		SITECORE_PAGES.PRODUCT_GUIDES as string,
	];
	const level2TransactionUrls = [
		SITECORE_PAGES.CONTRIBUTIONS as string,
		SITECORE_PAGES.CONSOLIDATE_SUPER as string,
	];
	const level2AccountUrlsForPending = [
		SITECORE_PAGES.WHO_GETS_YOUR_MONEY as string,
		SITECORE_PAGES.WHO_GETS_YOUR_BENEFIT as string,
		SITECORE_PAGES.PRODUCT_GUIDES as string,
	];
	const level2AccountUrlsForCIPending = [
		SITECORE_PAGES.WHO_GETS_YOUR_MONEY as string,
		SITECORE_PAGES.WHO_GETS_YOUR_BENEFIT as string,
		SITECORE_PAGES.PRODUCT_GUIDES as string,
	];

	let filterRestItems: MenuItem[];

	if (
		productClassification.toLowerCase() ===
			PRODUCT_CLASSIFICATION.ACCUMULATION.toLowerCase() &&
		balance === '0'
	) {
		filterRestItems = restItemsRaw.filter(
			a =>
				level2AccountUrlsForPending.includes(a.url) ||
				level2TransactionUrls.includes(a.url),
		);
	} else if (
		productClassification.toLowerCase() ===
		PRODUCT_CLASSIFICATION.ACCUMULATION.toLowerCase()
	) {
		filterRestItems = restItemsRaw.filter(
			a =>
				level2AccountUrls.includes(a.url) ||
				level2TransactionUrls.includes(a.url),
		);
	} else if (
		productClassification.toLowerCase() ===
		PRODUCT_CLASSIFICATION.CI.toLowerCase()
	) {
		filterRestItems = restItemsRaw.filter(
			a =>
				level2AccountUrlsForCIPending.includes(a.url) ||
				level2TransactionUrls.includes(a.url),
		);
	} else {
		filterRestItems = restItemsRaw.filter(a =>
			level2AccountUrls.includes(a.url),
		);
	}

	return filterRestItems;
};

/**
 * This method returns a boolean whether to show/hide an item on navigation menu
 * @param {string} productClassification - product classification of the selected account by logged in member.
 * @param {array} accountStatus - list of account types that are allowed see specific menu item.
 */
export function isPendingProfile(
	balance: string | number,
	productClassification: string,
	accountStatus: string,
) {
	if (!productClassification || !accountStatus) return false;

	const accumPending =
		productClassification.toLowerCase() ===
			PRODUCT_CLASSIFICATION.ACCUMULATION.toLowerCase() &&
		accountStatus.toLowerCase() === ACCOUNT_STATUS.ACTIVE.toLowerCase() &&
		balance === '0';
	const ciPending =
		productClassification.toLowerCase() ===
			PRODUCT_CLASSIFICATION.CI.toLowerCase() &&
		accountStatus.toLowerCase() === ACCOUNT_STATUS.PROVISIONAL.toLowerCase();
	const ttrPending =
		productClassification.toLowerCase() ===
			PRODUCT_CLASSIFICATION.TTR.toLowerCase() &&
		accountStatus.toLowerCase() === ACCOUNT_STATUS.PROVISIONAL.toLowerCase();

	return accumPending || ciPending || ttrPending;
}
